import { z } from 'zod';
/**
 * Prefix for a user-created saved view's opaque ordering key (e.g. `"saved:42"`).
 * The `saved:`/`preset:` key scheme is a contract shared by the API (ordering
 * reconciliation) and every frontend that renders views, so it lives here.
 */
export const SAVED_VIEW_KEY_PREFIX = 'saved:';
/** Prefix for a system-provided preset view's opaque ordering key (e.g. `"preset:my-assignments"`). */
export const PRESET_VIEW_KEY_PREFIX = 'preset:';
/** Builds the opaque ordering key for a saved (user-created) view. */
export const savedViewKey = (id) => `${SAVED_VIEW_KEY_PREFIX}${id}`;
/** Builds the opaque ordering key for a preset view from its stable preset id. */
export const presetViewKey = (presetKey) => `${PRESET_VIEW_KEY_PREFIX}${presetKey}`;
/** True if `key` references a saved (user-created) view rather than a preset. */
export const isSavedViewKey = (key) => key.startsWith(SAVED_VIEW_KEY_PREFIX);
/**
 * Maximum serialized byte length allowed for a saved view's `filterConfig`.
 * Keeps the generic JSON blob bounded so a single view cannot bloat the table.
 */
export const SAVED_VIEW_FILTER_CONFIG_MAX_BYTES = 8_000;
/**
 * Maximum number of saved (user-created) views allowed per user, government, and page.
 * Enforced in the controller to prevent unbounded growth.
 */
export const SAVED_VIEWS_PER_PAGE_LIMIT = 50;
/** Maximum number of visuals a single view may include (one row of up to 4). */
export const MAX_SAVED_VIEW_VISUALS = 4;
/**
 * The ordered list of visual keys selected for a view. Keys reference the
 * consuming page's code-defined visual catalog; the backend treats them as
 * opaque strings and only enforces the count cap.
 */
export const savedViewVisualConfigSchema = z
    .array(z.string().max(120))
    .max(MAX_SAVED_VIEW_VISUALS, `A view can include at most ${MAX_SAVED_VIEW_VISUALS} visuals`);
/**
 * A page's stable identifier. Scopes saved views and settings to a particular
 * dashboard page so the system can be reused across pages.
 */
export const savedViewPageKeySchema = z.string().min(1, 'Page key is required').max(100);
/**
 * Generic, page-agnostic filter payload. The backend stores this opaquely as JSON;
 * page-specific shape validation is the frontend's responsibility when a view is applied.
 */
export const savedViewFilterConfigSchema = z
    .record(z.string(), z.unknown())
    .refine((value) => JSON.stringify(value).length <= SAVED_VIEW_FILTER_CONFIG_MAX_BYTES, {
    message: `Filter configuration exceeds the maximum size of ${SAVED_VIEW_FILTER_CONFIG_MAX_BYTES} bytes`,
});
export const savedViewCreateSchema = z.object({
    pageKey: savedViewPageKeySchema,
    name: z.string().min(1, 'Name is required').max(255),
    filterConfig: savedViewFilterConfigSchema,
    visualConfig: savedViewVisualConfigSchema.optional(),
});
export const savedViewUpdateSchema = z
    .object({
    name: z.string().min(1, 'Name is required').max(255).optional(),
    filterConfig: savedViewFilterConfigSchema.optional(),
    visualConfig: savedViewVisualConfigSchema.optional(),
})
    .refine((data) => data.name !== undefined || data.filterConfig !== undefined || data.visualConfig !== undefined, { message: 'At least one field must be provided' });
/**
 * Per-user, per-page ordering and favorite preferences. `viewOrder` holds opaque
 * view keys (e.g. `"preset:my-assignments"`, `"saved:42"`); `favoriteKey` points at
 * the default view. Both fields are optional so the endpoint can update either
 * independently, but at least one must be present.
 */
export const savedViewSettingsSchema = z
    .object({
    viewOrder: z.array(z.string().max(200)).max(200).optional(),
    favoriteKey: z.string().max(200).nullable().optional(),
})
    .refine((data) => data.viewOrder !== undefined || data.favoriteKey !== undefined, {
    message: 'At least one of viewOrder or favoriteKey must be provided',
});
